import { updateIngredientOptions } from "../../OrderFlow/hooks/RecipeSelection/recipeSelectionUtils";
import { createKey } from "../../Shared/utils/createKey";
/**
 * Calculates the weekly breakdown of meals, extra meals, recipes, and products based on a given array of days.
 * @param {MyMealsDayType[]} days - Array of day objects to calculate the weekly breakdown from.
 * @returns {WeekBreakdown | undefined} An object containing aggregated quantities and balances for the week, or undefined if there are no days.
 */
export const getWeekBreakdown = (days) => {
    if (!days) {
        return undefined;
    }
    let tempWeeklyExtraMealsQuantity = 0;
    let tempWeeklyTotalMealsQuantity = 0;
    let tempWeeklyRecipeForMealsQuantity = 0;
    let tempWeeklyProductsQuantity = 0;
    let tempWeeklyMealsBalance = 0;
    let tempWeeklyProductBalance = 0;
    days.forEach((day) => {
        const dayBreakdown = getDayBreakdown(day);
        tempWeeklyExtraMealsQuantity += dayBreakdown.quantity.extraMeal;
        tempWeeklyRecipeForMealsQuantity += dayBreakdown.quantity.recipe;
        tempWeeklyTotalMealsQuantity += dayBreakdown.quantity.meal;
        tempWeeklyProductsQuantity += dayBreakdown.quantity.productAdded - dayBreakdown.quantity.productRemoved;
        tempWeeklyMealsBalance += dayBreakdown.balance.meal;
        tempWeeklyProductBalance += dayBreakdown.balance.product;
    });
    return {
        quantity: {
            meal: tempWeeklyTotalMealsQuantity,
            extraMeal: tempWeeklyExtraMealsQuantity,
            recipe: tempWeeklyRecipeForMealsQuantity,
            product: tempWeeklyProductsQuantity,
        },
        balance: {
            meal: tempWeeklyMealsBalance,
            product: tempWeeklyProductBalance,
            total: tempWeeklyMealsBalance + tempWeeklyProductBalance,
        },
    };
};
/**
 * Calculates the breakdown of meals, recipes, and products for a single day.
 * @param {MyMealsDayType} day - The day object to calculate the breakdown for.
 * @returns {DayBreakdown} An object containing quantities for meals, recipes, and products for the day.
 */
export const getDayBreakdown = (day) => {
    const dayMealQuantitySum = day.Meals.reduce((total, meal) => total + meal.Quantity, 0);
    const dayExtraMealQuantity = day.Meals.map((meal) => {
        if (meal.OriginalQuantity > meal.Quantity) {
            return 0;
        }
        return meal.Quantity - meal.OriginalQuantity;
    }).reduce((total, extraMealQuantity) => total + extraMealQuantity, 0);
    let dayMealBalance = day.Meals.map((meal) => {
        if (meal.OriginalQuantity === meal.Quantity) {
            return 0;
        }
        return (meal.Quantity - meal.OriginalQuantity) * meal.Price;
    }).reduce((total, mealPrice) => total + mealPrice, 0);
    dayMealBalance += day.Recipes.map((recipe) => {
        var _a, _b;
        if (recipe.OriginalQuantity === recipe.Quantity) {
            return 0;
        }
        let recipePrice = ((_a = recipe.RecipeAssignmentPrice) !== null && _a !== void 0 ? _a : 0) - ((_b = recipe.ProgramMealPrice) !== null && _b !== void 0 ? _b : 0);
        return (recipe.Quantity - recipe.OriginalQuantity) * recipePrice;
    }).reduce((total, recipePrice) => total + recipePrice, 0);
    const dayAllRecipeSum = day.Recipes.reduce((total, recipe) => total + recipe.Quantity, 0);
    const dayProductBalance = day.Products.map((product) => {
        if (product.NewQuantity === product.PurchasedQuantity) {
            return 0;
        }
        return (product.NewQuantity - product.PurchasedQuantity) * product.ProductPrice;
    }).reduce((total, productPrice) => total + productPrice, 0);
    const productsToPurchaseQuantity = day.Products.map((product) => {
        if (product.NewQuantity <= product.PurchasedQuantity) {
            return 0;
        }
        return product.NewQuantity - product.PurchasedQuantity;
    }).reduce((total, quantity) => total + quantity, 0);
    const dayRemovedProducts = day.Products.reduce((total, product) => {
        if (product.PurchasedQuantity <= product.NewQuantity) {
            return total;
        }
        return total + (product.PurchasedQuantity - product.NewQuantity);
    }, 0);
    const dayRemovedProductBalance = day.Products.map((product) => {
        if (product.NewQuantity >= product.PurchasedQuantity) {
            return 0;
        }
        return (product.NewQuantity - product.PurchasedQuantity) * product.ProductPrice;
    }).reduce((total, productPrice) => total + productPrice, 0);
    const dayAddedProductBalance = day.Products.map((product) => {
        if (product.NewQuantity <= product.PurchasedQuantity) {
            return 0;
        }
        return (product.NewQuantity - product.PurchasedQuantity) * product.ProductPrice;
    }).reduce((total, productPrice) => total + productPrice, 0);
    const dayPurchasedProductQuantity = day.Products.filter((p) => p.PurchasedQuantity > 0).reduce((total, product) => total + product.PurchasedQuantity, 0);
    return {
        quantity: {
            meal: dayMealQuantitySum,
            extraMeal: dayExtraMealQuantity,
            recipe: dayAllRecipeSum,
            productAdded: productsToPurchaseQuantity,
            productPurchased: dayPurchasedProductQuantity,
            productRemoved: dayRemovedProducts,
            product: dayPurchasedProductQuantity - dayRemovedProducts,
        },
        balance: {
            meal: dayMealBalance,
            product: dayProductBalance,
            productRemoved: dayRemovedProductBalance,
            productAdded: dayAddedProductBalance,
        },
    };
};
/**
 * Determines if there's a difference in quantity between the original selections and the current selections.
 * @param {MyMealsDayType[]} days - Array of current day selections.
 * @param {WeekBreakdown["quantity"]} originalSelectionQuantity - The original quantities selected for the week.
 * @returns {boolean} A boolean indicating whether there's a difference in quantity between the original and current selections.
 */
export const hasQuantityDiff = (days, originalSelectionQuantity) => {
    if (days) {
        if (originalSelectionQuantity) {
            const weekBreakdown = getWeekBreakdown(days);
            if (!weekBreakdown)
                return false;
            const meals = weekBreakdown.quantity.meal;
            const recipes = weekBreakdown.quantity.recipe;
            const products = weekBreakdown.quantity.product;
            if (originalSelectionQuantity.meal !== meals ||
                originalSelectionQuantity.recipe !== recipes ||
                originalSelectionQuantity.product !== products ||
                weekBreakdown.quantity.extraMeal !== 0) {
                return true;
            }
        }
    }
    return false;
};
/**
 *  With ad hoc meal selection, the meal quantity is always updated to match the selected recipe quantity
 *
 * @param days
 * @param selectedDay
 * @param programMealId
 */
export const doAdHocChanges = (days, selectedDay, programMealId) => {
    const targetDay = days.find((day) => day.DateFormatted === selectedDay.DateFormatted);
    const targetMeal = targetDay === null || targetDay === void 0 ? void 0 : targetDay.Meals.find((meal) => meal.ProgramMealId === programMealId);
    const newQuantity = targetDay === null || targetDay === void 0 ? void 0 : targetDay.Recipes.filter((recipe) => recipe.ProgramMealId === programMealId).reduce((sum, recipe) => sum + recipe.Quantity, 0);
    if (targetMeal)
        targetMeal.Quantity = newQuantity !== null && newQuantity !== void 0 ? newQuantity : 0;
};
export const shapeRecipes = (recipes) => {
    return recipes.map((r) => {
        const totalQuantity = r.MyMealsAssignments
            ? r.MyMealsAssignments.reduce((total, assignment) => total + assignment.Quantity, 0)
            : 0;
        const assignments = r.MyMealsAssignments
            ? r.MyMealsAssignments.map((ma) => {
                return Object.assign(Object.assign({}, ma), { SelectedIngredientOptions: ma.SelectedIngredientOptions, AssignmentId: createKey() });
            })
            : [];
        return Object.assign(Object.assign({}, r), { RecipeIngredients: updateIngredientOptions(r.RecipeIngredients), Quantity: totalQuantity, Assignments: assignments, MyMealsAssignments: null });
    });
};
